/**
 * Add / Edit Admin User Component
 * ------------------------------------
 * Handles:
 * - Creating new admin users
 * - Editing existing admin users
 * - Updating verification status
 * - Managing order types (multi-select)
 * - Resetting form and clearing edit mode
 */

import React, { useState, useEffect, useMemo } from "react";
import {
    TextField,
    Button,
    Chip,
    Box,
    Grid,
    FormControlLabel,
    Switch,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    Typography,
    InputAdornment,
    useTheme,
} from "@mui/material";

import AdminApi from "Service/Admin.api";
import { StyledTableCell } from "./ProfessionalUserList";
import ColorData from "Components/CstmTheme/ColorData";
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action";

/* ------------------------------
   INTERFACES
------------------------------ */
interface OrderType {
    id: number;
    name: string;
}

interface UserFormType {
    firstName: string;
    lastName: string;
    email: string;
    contactNumber: string;
    bverified: boolean;
}



interface userType {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    contactNumber: string;
    bverified?: boolean;
}

/* ------------------------------
   MAIN COMPONENT
------------------------------ */

const AddAdminUser: React.FC = () => {
    const navigate = useNavigate();
    
    /* ----------------------------------
       LOCAL STATE
    ---------------------------------- */
    const [formData, setFormData] = useState<UserFormType>({
        bverified: false, // Default verified OFF for new admins
        firstName: "",
        lastName: "",
        email: "",
        contactNumber: "",
    });

    const [adminList, setAdminList] = useState<userType[]>([]);
    const [editId, setEditId] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const theme = useTheme();
    const dispatch = useDispatch();

    /* ----------------------------------
       EFFECTS - Load Data Once
    ---------------------------------- */
// 1. Keep your original data fetching useEffect
useEffect(() => {
    loadAllUsers();
}, []);

// 2. Use useMemo to filter the list dynamically based on search
const displayedAdmins = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    
    // If no search query, show the full list
    if (!query) return adminList;

    return adminList.filter((adminItem) => {
        const fullName = `${adminItem.firstName} ${adminItem.lastName}`.toLowerCase();
        const phone = (adminItem.contactNumber || "").toLowerCase();
        const email = (adminItem.email || "").toLowerCase();

        return (
            fullName.includes(query) ||
            phone.includes(query) ||
            email.includes(query)
        );
    });
}, [searchQuery, adminList]); // Re-runs whenever search or the main list changes

    /* ----------------------------------
       Fetch Admin List
    ---------------------------------- */
    const loadAllUsers = async () => {
        dispatch(startLoading({ loading: true, loadtext: "Loading users..." }));
        try {
            const res = await AdminApi.getAllUsers();
            if (res.data) {
                setAdminList(res.data);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            dispatch(
                setAlertData({
                    alertMessage: "Error fetching users",
                    alertVariant: "error",
                    openAlert: true,
                })
            );
        }
        finally {
            dispatch(stopLoading());
        }
    };

    /* ----------------------------------
       Handle Basic Form Input Change
    ---------------------------------- */
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };



    /* ----------------------------------
       Verified Toggle
    ---------------------------------- */
    const handleVerifiedToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({
            ...prev,
            bverified: event.target.checked,
        }));
    };

    /* ----------------------------------
       Reset Form (Reusable)
    ---------------------------------- */
    const handleReset = () => {
        setFormData({
            firstName: "",
            lastName: "",
            email: "",
            contactNumber: "",
            bverified: formData.bverified ?? false,
        });
        setEditId(null);
    };

    /* ----------------------------------
       Submit Form (Create / Update)
    ---------------------------------- */
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        dispatch(startLoading({ loading: true, loadtext: "Updating verified status..." }));
        try {
            if (editId) {
                await AdminApi.updateUserVerifiedStatus({
                    "userId": editId,
                    "bVerified": formData.bverified
                });

                dispatch(stopLoading());
                dispatch(
                    setAlertData({
                        alertMessage: "Verified Status Updated Successfully",
                        alertVariant: "success",
                        openAlert: true,
                    })
                );
            } 
            await loadAllUsers();
            handleReset();
        } catch (error) {
            console.error("Error saving admin:", error);
            dispatch(
                setAlertData({
                    alertMessage: "Error Updating Verified Status",
                    alertVariant: "error",
                    openAlert: true,
                })
            );
        }

    };

    /* ----------------------------------
       Handle Edit (Prefill Form)
    ---------------------------------- */
    const handleEdit = (item: userType) => {
        setEditId(item.id);

        setFormData({
            firstName: item.firstName,
            lastName: item.lastName,
            email: item.email,
            contactNumber: item.contactNumber,
            bverified: item.bverified ?? false,
            // verified: item.verified ?? true,
        });
        
        // scroll top
        window.top?.scrollTo(0, 0);

    };

    /* ----------------------------------
       RENDER
    ---------------------------------- */
    return (
        <Box sx={{
            py: 4,
            pb: 12,               // Added extra padding at bottom for better visibility
            display: "flex",
            flexDirection: "column",
            height: "100vh",      // LIMITS height to screen size
            overflowY: "auto",    // ALLOWS scrolling within this container
        }}>
            {/* 3. ADDED BACK BUTTON HERE */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate(-1)}
                    sx={{ fontWeight: 'bold' }}
                >
                    Back
                </Button>
            </Box>

            {editId && (
                <Box display="flex" justifyContent="center" my={4} width="100%">
                    {/* if edit then open this form */}
                    <Box width="100%" maxWidth="800px" p={4} component={Paper} elevation={3} borderRadius={3}>
                        <h2 style={{ textAlign: "center", marginBottom: "20px" }}>Update User</h2>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                {editId && (
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="First Name"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            // make this field readonly
                                            inputProps={{ readOnly: true }}
                                            
                                        />
                                    </Grid>
                                )}
                                {editId && (
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Last Name"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            // make this field readonly
                                            inputProps={{ readOnly: true }}
                                        />
                                    </Grid>
                                )}
                                {editId && (
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            // make this field readonly
                                            inputProps={{ readOnly: true }}
                                        />
                                    </Grid>
                                )}
                                {editId && (
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth
                                            label="Contact Number"
                                            name="contactNumber"
                                            value={formData.contactNumber}
                                            onChange={handleChange}
                                            // make this field readonly
                                            inputProps={{ readOnly: true }}
                                        />
                                    </Grid>
                                )}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.bverified}
                                                onChange={handleVerifiedToggle}
                                                color="success"
                                            />

                                        }
                                        label={formData.bverified ? "Verified" : "Not Verified"}
                                    />
                                </Grid>




                            <Grid size={{ xs: 12 }}>
                                <Grid sx={{ xs: 12, display: "flex", gap: 2 }} >
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        sx={{ py: 1.2, fontSize: "16px" }}
                                    >
                                        Update User
                                    </Button>

                                    <Button
                                        type="button"
                                        variant="outlined"
                                        color="primary"
                                        fullWidth
                                        sx={{ py: 1.2, fontSize: "16px" }}
                                        onClick={handleReset}
                                    >
                                        Cancel
                                    </Button>
                                </Grid>
                            </Grid>

                            </Grid>
                        </form>
                    </Box>
                </Box>
            )}

            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{
                padding: 2,
                borderRadius: 2,
                backgroundColor: theme => ColorData.primary.main,
                color: theme => ColorData.text.secondary,
                
            }}>
                Find User
            </Typography>

            {/* Search and Actions */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 8 }}>
                    {/* <SearchCard elevation={2}>
                                            <CardContent> */}
                    <TextField
                        fullWidth
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search professional User..."
                        variant="outlined"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="primary" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main,
                                },
                            },
                        }}
                    />
                    {/* </CardContent>
                                        </SearchCard> */}
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Box display="flex" gap={1} height="100%" alignItems="center">
                        <Chip
                            icon={<PersonIcon />}
                            label={`Total Users: ${adminList.length}`}
                            color="info"
                            variant="outlined"
                            sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                height: { xs: 28, sm: 32 }
                            }}
                        />
                    </Box>
                </Grid>
            </Grid>




            <h2 style={{ marginTop: "40px", textAlign: "center", }}>All User List</h2>

            {/* LISTING TABLE */}
            <Paper sx={{px:2}}>
                <TableContainer
                    sx={{
                        maxHeight: 470,
                        overflowY: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                    }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>User ID</StyledTableCell>
                                <StyledTableCell>First Name</StyledTableCell>
                                <StyledTableCell>Last Name</StyledTableCell>
                                <StyledTableCell>Email</StyledTableCell>
                                <StyledTableCell>Contact</StyledTableCell>
                                <StyledTableCell>User Role</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell align="center">Action</StyledTableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {displayedAdmins.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        No Users Found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                displayedAdmins.map((user: any, index: number) => (
                                    <TableRow key={user.id}>
                                        <TableCell>{user.id}</TableCell>
                                        <TableCell>{user.firstName}</TableCell>
                                        <TableCell>{user.lastName}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.contactNumber}</TableCell>
                                        <TableCell>{user.userType}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.bverified ? "Verified" : "Not Verified"}
                                                color={user.bverified ? "success" : "error"}
                                                size="small"
                                            />
                                        </TableCell>

                                        {/* CONDITIONAL ACTION BUTTON */}
                                        <TableCell align="center">
                                            <Button variant="outlined" size="small" onClick={() => handleEdit(user)}>Edit</Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default AddAdminUser;
